import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import {
  setMode,
  setConversations,
  setConversationsLoading,
  setActiveConversation,
  removeConversation,
  startNewChat,
} from "@redux/slices/archiSlice";
import { listConversations, deleteConversation } from "./api";
import ArchiConversationList from "./ArchiConversationList";
import ArchiChatPane from "./ArchiChatPane";
import { isProductionEnvironment } from "@shared/navigation/SideMenu/sideMenuEnv";

/**
 * Right-side sidebar for Archi. Three modes:
 *   collapsed  — off-screen, FAB visible (rendered separately)
 *   open       — ~50% viewport (max 720px), full height
 *   expanded   — fills available width minus the left SideMenu
 *
 * The sidebar sits at z-index 30 so it stays above page content but below
 * any future modal stack. It pushes nothing — overlays the dashboard.
 */
const ArchiSidebar: React.FC = () => {
  const dispatch = useAppDispatch();
  const api = useAxiosAuth0();
  const { isAuthenticated } = useAuth0();

  const mode = useAppSelector((s) => s.archi.mode);
  const conversations = useAppSelector((s) => s.archi.conversations);
  const activeId = useAppSelector((s) => s.archi.activeConversationId);

  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const pendingConv =
    confirmDelete !== null
      ? conversations.find((c) => c.id === confirmDelete)
      : null;

  const loadConversations = React.useCallback(async () => {
    if (!isAuthenticated) return;
    dispatch(setConversationsLoading(true));
    try {
      const rows = await listConversations(api);
      dispatch(setConversations(rows));
    } catch (err) {
      console.error("[Archi] failed to load conversations", err);
    } finally {
      dispatch(setConversationsLoading(false));
    }
  }, [api, dispatch, isAuthenticated]);

  useEffect(() => {
    if (mode !== "collapsed") loadConversations();
  }, [mode, loadConversations]);

  const handleDelete = async (id: number) => {
    setDeleting(true);
    try {
      await deleteConversation(api, id);
      dispatch(removeConversation(id));
    } catch (err) {
      console.error("[Archi] delete failed", err);
    } finally {
      setDeleting(false);
      setConfirmDelete(null);
    }
  };

  // PROD GATE: defense-in-depth pair to ArchiFab.tsx. To enable Archi
  // in prod, REMOVE this line (and the matching guard in ArchiFab.tsx).
  if (isProductionEnvironment()) return null;
  if (mode === "collapsed") return null;

  const widthClass =
    mode === "expanded"
      ? "w-[calc(100vw_-_var(--sidemenu-width,260px))]"
      : "w-full sm:w-[min(720px,55vw)]";

  return (
    <aside
      className={`
        fixed right-0 top-0 z-30
        h-screen
        ${widthClass}
        bg-semantic-bg-primary
        border-l border-semantic-border-secondary
        shadow-2xl
        flex flex-col
        transition-[width] duration-200
      `}
      aria-label="Archi AI"
    >
      {/* Header */}
      <header className="
        shrink-0
        flex items-center justify-between
        px-4 py-3
        border-b border-semantic-border-secondary
      ">
        <div className="flex items-center gap-2">
          <span className="
            inline-flex items-center justify-center
            w-7 h-7 rounded-full
            bg-semantic-bg-brand-bold-secondary text-white
            text-xs font-semibold
          ">A</span>
          <div>
            <div className="text-sm font-semibold text-semantic-text-primary leading-tight">
              Archi AI
            </div>
            <div className="text-[11px] text-semantic-text-secondary leading-tight">
              {activeId ? `Chat #${activeId}` : "New chat"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => dispatch(startNewChat())}
            className="px-2 py-1 text-xs rounded hover:bg-semantic-state-active text-semantic-text-secondary"
            title="New chat"
          >
            + New
          </button>
          <button
            type="button"
            onClick={() =>
              dispatch(setMode(mode === "expanded" ? "open" : "expanded"))
            }
            className="p-1.5 rounded hover:bg-semantic-state-active text-semantic-text-secondary"
            title={mode === "expanded" ? "Collapse to side" : "Expand"}
            aria-label={mode === "expanded" ? "Collapse" : "Expand"}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              {mode === "expanded" ? (
                <path
                  d="M2 8h12M6 4l-4 4 4 4M14 4l-4 4 4 4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              ) : (
                <path
                  d="M2 8h12M2 4l4 4-4 4M14 4l-4 4 4 4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              )}
            </svg>
          </button>
          <button
            type="button"
            onClick={() => dispatch(setMode("collapsed"))}
            className="p-1.5 rounded hover:bg-semantic-state-active text-semantic-text-secondary"
            title="Close"
            aria-label="Close"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path
                d="M3 3l10 10M13 3L3 13"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      </header>

      {/* Body: conversation list (left column, always visible) + chat pane (right). */}
      <div className="flex-1 min-h-0 flex">
        <ArchiConversationList
          conversations={conversations}
          activeId={activeId}
          onSelect={(id) => dispatch(setActiveConversation(id))}
          onNew={() => dispatch(startNewChat())}
          onDelete={(id) => setConfirmDelete(id)}
          compact={mode === "open"}
        />
        <div className="flex-1 min-w-0 flex flex-col">
          <ArchiChatPane />
        </div>
      </div>

      <GenericAlert
        isOpen={confirmDelete !== null}
        onClose={() => !deleting && setConfirmDelete(null)}
        onConfirm={() =>
          confirmDelete !== null && handleDelete(confirmDelete)
        }
        title="Delete this chat?"
        description={
          pendingConv ? (
            <span>
              <span className="font-medium">
                {pendingConv.title || "Untitled"}
              </span>{" "}
              and all of its messages will be permanently removed.
            </span>
          ) : (
            "This cannot be undone."
          )
        }
        confirmLabel={deleting ? "Deleting…" : "Delete"}
        cancelLabel="Cancel"
        variant="warning"
        confirmButtonDisabled={deleting}
      />
    </aside>
  );
};

export default ArchiSidebar;
